import { Button, Swiper } from 'antd-mobile';
import styled from 'styled-components';
import { Image, Space, Tag, Typography } from 'antd';
import { StarFilled } from '@ant-design/icons';
import NotifResult from 'components/global/NotifResult';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DynamicIcon from '../../../components/dynamic-icon';
import useTranslations from '../../../hooks/useTranslations';
import PageMobile from '../../../components/page-mobile';
import TherapistContextProvider from './context/TherapistContextProvider';
import { useTherapistCtx } from './context/TherapistContext';
import AboutTherapist from './component/AboutTherapist';
import ReviewsTherapist from './component/ReviewsTherapist';
import GalleryTherapist from './component/GalleryTherapist';
import Segmented from '../../../components/segmented';
import FallbackImage from '../../../assets/img/ThaiMassage.png';
import trackMetaEvent from '../../../helpers/metaPixel';

const { Text } = Typography;

const StyledBottomBar = styled.div`
    background: white;
    position: fixed;
    bottom: 0;
    display: flex;
    align-items: center;
    padding: 16px;
    width: 100%;
    max-width: 430px;
`;

const ImageStyled = styled.div`
    height: 220px;
    .ant-image {
        width: 100%;
        height: 100%;
        aspect-ratio: 21 / 9;
    }
`;

const SwiperStyled = styled(Swiper)`
    .adm-swiper-indicator {
        padding: 0 4px 8px 4px;
        width: 100%;
        .adm-page-indicator-dot {
            width: 100%;
            display: none;
        }
    }
`;

const SpaceStyled = styled(Space)`
    .ant-space-item:first-child {
        line-height: 1.2;
    }
`;

function BottomBar() {
    const { t } = useTranslations();
    const { masseusesId } = useParams();
    const { therapist } = useTherapistCtx();
    const navigate = useNavigate();

    const textBookAppointment = t('BookAppointment', 'Book Appointment');

    return (
        <StyledBottomBar>
            <Button
                disabled={therapist?.visibility_status === 0}
                onClick={() => {
                    if (therapist?.distance_km >= 50) {
                        NotifResult({
                            type: 'error',
                            message: t('DistanceTooFar', 'Distance Too Far'),
                            description: t('YourCurrentLocationIsOutsideOurServiceArea', 'Your current location is outside our service area'),
                        });
                        return;
                    }
                    trackMetaEvent('InitiateCheckout', {
                        therapist_id: masseusesId,
                        cta_source: 'guest_therapist_detail',
                        booking_mode: 'immediate',
                    });
                    navigate(`/booking-detail/${masseusesId}`);
                }}
                color="primary"
                style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'white',
                        gap: 9,
                    }}
                >
                    <Text style={{ color: 'white' }}>{textBookAppointment}</Text>
                </div>
            </Button>
        </StyledBottomBar>
    );
}

function TherapistDetail() {
    const { t } = useTranslations();
    const { therapist } = useTherapistCtx();
    const [activeTab, setActiveTab] = useState('about');
    const [visibleImage, setVisibleImage] = useState(false);

    const textAbout = t('About', 'About');
    const textGallery = t('Gallery', 'Gallery');
    const textReview = t('Review', 'Review');

    return (
        <PageMobile containerBody={false} isPageHeader customBottomBar={<BottomBar />}>
            <div style={{ minHeight: '100vh' }}>
                <SwiperStyled
                    indicatorProps={{
                        color: 'white',
                    }}
                    defaultIndex={0}
                >
                    <Swiper.Item>
                        <div
                            onClick={() => {
                                setVisibleImage(true);
                            }}
                            style={{
                                position: 'relative',
                                width: '100%',
                                height: 220,
                                overflow: 'hidden',
                            }}
                        >
                            <ImageStyled>
                                <Image
                                    className="swiper-image"
                                    fallback={FallbackImage}
                                    preview={{
                                        visible: visibleImage,
                                        onVisibleChange: (value) => {
                                            setVisibleImage(value);
                                        },
                                    }}
                                    style={{
                                        height: '100%',
                                        width: '100%',
                                        objectFit: 'cover',
                                        display: 'block',
                                    }}
                                    src={therapist?.profile_picture || FallbackImage}
                                />
                            </ImageStyled>
                            <div
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    background: 'linear-gradient(to top, #0E3D39,transparent)',
                                }}
                            />
                            <div
                                style={{
                                    position: 'absolute',
                                    bottom: 32,
                                    left: 12,
                                    right: 12,
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: 16,
                                }}
                            >
                                <SpaceStyled direction="vertical">
                                    <Text style={{ color: 'white' }}>
                                        {therapist?.full_name}, {therapist?.age}
                                    </Text>
                                    <div>
                                        <Space>
                                            <Tag
                                                style={{
                                                    border: 'unset',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                    color: '#fff',
                                                }}
                                                icon={<StarFilled style={{ color: '#FFA500' }} />}
                                            >
                                                {therapist?.rating}
                                            </Tag>
                                            <Tag
                                                style={{
                                                    border: 'unset',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                    color: '#fff',
                                                }}
                                                icon={<DynamicIcon width={14} height={14} iconName="mappin" />}
                                            >
                                                {therapist?.distance_km?.toFixed(2)} Km
                                            </Tag>
                                        </Space>
                                    </div>
                                </SpaceStyled>
                            </div>
                        </div>
                    </Swiper.Item>
                    {/* )} */}
                </SwiperStyled>
                <div style={{ padding: 16, height: '100%' }}>
                    <div style={{ width: '100%' }}>
                        <Segmented
                            options={[
                                { label: textAbout, value: 'about' },
                                { label: textGallery, value: 'gallery' },
                                { label: textReview, value: 'review' },
                            ]}
                            onChange={(value) => {
                                setActiveTab(value);
                            }}
                        />
                    </div>

                    {activeTab === 'about' && <AboutTherapist />}
                    {activeTab === 'gallery' && <GalleryTherapist />}
                    {activeTab === 'review' && <ReviewsTherapist />}
                </div>
            </div>
        </PageMobile>
    );
}

function TherapistDetail_() {
    return (
        <TherapistContextProvider>
            <TherapistDetail />
        </TherapistContextProvider>
    );
}

export default TherapistDetail_;
